// Partner Directory types

export type PartnerType = "service" | "integration";

export type IndustryType =
  | "payroll_hcm"
  | "employee_benefits"
  | "earned_wage_access"
  | "broker"
  | "401k"
  | "pos"
  | "cobra"
  | "ats"
  | "expenses"
  | "time_keeping"
  | "labor_management"
  | "general_ledger"
  | "employee_ancillary";

export type RelationshipTileKey =
  | "data_migration"
  | "integration"
  | "implementation"
  | "hr_services";

export interface RelationshipTileDisplay {
  key: RelationshipTileKey;
  display: string;
}

export interface Partner {
  id: number;
  name: string;
  slug: string;
  partner_type: PartnerType;
  partner_type_display: string;
  industry_type: IndustryType;
  industry_type_display: string;
  tile_description: string;
  is_featured: boolean;
  logo_url: string;
}

export interface PartnerDetail extends Partner {
  details_description: string;
  partnership_description: string;
  relationship_tiles: RelationshipTileKey[];
  relationship_tiles_display: RelationshipTileDisplay[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RelatedPartner {
  id: number;
  name: string;
  slug: string;
  industry_type: IndustryType;
  industry_type_display: string;
}

export interface PartnershipInquiryRequest {
  partner_slug?: string;
  partner_name_other?: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  message: string;
}

export interface PartnershipInquiryResponse {
  message: string;
  id: number;
}

export interface PartnerFilters {
  search?: string;
  partner_type?: PartnerType | "";
  industry_type?: IndustryType | "";
  featured?: boolean;
  sort?: "name" | "created_at" | "-name" | "-created_at";
  page?: number;
  page_size?: number;
}

// Admin types
export interface PartnerAdmin extends PartnerDetail {
  tile_description: string;
  logo_s3_path: string;
  logo_file: string;
}

export interface PartnerCreateRequest {
  name: string;
  slug?: string;
  partner_type: PartnerType;
  industry_type: IndustryType;
  tile_description: string;
  details_description: string;
  partnership_description: string;
  relationship_tiles: RelationshipTileKey[];
  is_featured: boolean;
  is_active: boolean;
  logo_s3_path: string;
  logo_file: string;
}

export interface PartnerUpdateRequest extends Partial<PartnerCreateRequest> {}

export interface LogoUploadResponse {
  s3_path: string;
  url: string;
}

// Badge color configuration
export interface BadgeColorConfig {
  background: string;
  text: string;
}

export const PARTNER_TYPE_COLORS: Record<PartnerType, BadgeColorConfig> = {
  service: { background: "#3F516B", text: "#FFFFFF" },
  integration: { background: "#3B7170", text: "#FFFFFF" },
};

export const FEATURED_BADGE_COLOR: BadgeColorConfig = {
  background: "#FFE08A",
  text: "#3C4046",
};

export const INDUSTRY_COLORS: Record<IndustryType, BadgeColorConfig> = {
  payroll_hcm: { background: "#E3F0FA", text: "#3C4046" },
  employee_benefits: { background: "#F8E0CE", text: "#3C4046" },
  earned_wage_access: { background: "#CAE4E9", text: "#3C4046" },
  broker: { background: "#D1DBEE", text: "#3C4046" },
  "401k": { background: "#DCEED2", text: "#3C4046" },
  pos: { background: "#DFF4EF", text: "#3C4046" },
  cobra: { background: "#EEE2D1", text: "#3C4046" },
  ats: { background: "#C6C7EA", text: "#3C4046" },
  expenses: { background: "#DEEDC8", text: "#3C4046" },
  time_keeping: { background: "#C4D8ED", text: "#3C4046" },
  labor_management: { background: "#EBCFCF", text: "#3C4046" },
  general_ledger: { background: "#F3EFCB", text: "#3C4046" },
  employee_ancillary: { background: "#EDDBF2", text: "#3C4046" },
};

// Relationship tile configuration
export interface RelationshipTileConfig {
  key: RelationshipTileKey;
  title: string;
  description: string;
  icon: string; // MUI icon name
}

export const RELATIONSHIP_TILES: RelationshipTileConfig[] = [
  {
    key: "data_migration",
    title: "Data Migration",
    description: "Move data between systems to keep payroll and HR aligned.",
    icon: "Send",
  },
  {
    key: "integration",
    title: "Integration",
    description: "Connect systems to automate data flow across platforms.",
    icon: "Sync",
  },
  {
    key: "implementation",
    title: "Implementation & Consulting",
    description: "Support for setup, configuration, and ongoing system use.",
    icon: "CalendarToday",
  },
  {
    key: "hr_services",
    title: "HR Services",
    description: "Support across core HR processes.",
    icon: "Monitor",
  },
];

// Display mappings
export const INDUSTRY_DISPLAY_NAMES: Record<IndustryType, string> = {
  payroll_hcm: "Payroll/HCM",
  employee_benefits: "Employee Benefits",
  earned_wage_access: "Earned Wage Access",
  broker: "Broker",
  "401k": "401K",
  pos: "Point of Sale",
  cobra: "COBRA",
  ats: "Applicant Tracking",
  expenses: "Expenses",
  time_keeping: "Timekeeping",
  labor_management: "Labor Management",
  general_ledger: "General Ledger",
  employee_ancillary: "Employee Ancillary",
};

export const PARTNER_TYPE_DISPLAY_NAMES: Record<PartnerType, string> = {
  service: "Service Partner",
  integration: "Integration Partner",
};
